import { useEffect, useState } from 'react';

import { Checkbox, FormControlLabel } from '@mui/material';
import { SquareCheck } from 'lucide-react';

import { CheckboxFilterPanel } from '@modules/_shared/components/filters/quick-filters/CheckboxFilterPanel';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { useLocale } from '@shared/hooks/locale';

export type NullCheckMode = 'disabled' | 'not-null' | 'is-null';

export const getBooleanDefaultValue = (
  valueFromPath: any,
  isNullable?: boolean,
  nullCheckMode: NullCheckMode = 'disabled',
) => {
  if (nullCheckMode === 'not-null') {
    return {
      defaultTrueChecked: valueFromPath?._is_null === false,
      defaultFalseChecked: valueFromPath?._is_null === true,
      defaultNullChecked: false,
    };
  }

  if (nullCheckMode === 'is-null') {
    return {
      defaultTrueChecked: valueFromPath?._is_null === true,
      defaultFalseChecked: valueFromPath?._is_null === false,
      defaultNullChecked: false,
    };
  }

  const defaultTrueChecked =
    valueFromPath?._eq === true ||
    valueFromPath?._in?.includes(true) ||
    valueFromPath?._neq === false;
  const defaultFalseChecked =
    valueFromPath?._eq === false ||
    valueFromPath?._in?.includes(false) ||
    valueFromPath?._neq === true;
  const defaultNullChecked =
    isNullable &&
    (valueFromPath?._is_null || typeof valueFromPath?._neq === 'boolean');

  return {
    defaultTrueChecked,
    defaultFalseChecked,
    defaultNullChecked,
  };
};

export const BooleanFilter = ({
  label,
  path,
  addWhereClause,
  deleteWhereClause,
  where,
  queryParamsScope,
  disabled,
  isNullable = true,
  nullCheckMode = 'disabled',
}: QuickFilterProps) => {
  const { t } = useLocale();
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { getValueFromPath } = useSchema();
  const initialValue = getValueFromPath(path, where);
  const { defaultTrueChecked, defaultFalseChecked, defaultNullChecked } =
    getBooleanDefaultValue(initialValue, isNullable, nullCheckMode);

  const [trueChecked, setTrueChecked] = useState(defaultTrueChecked);
  const [falseChecked, setFalseChecked] = useState(defaultFalseChecked);
  const [nullChecked, setNullChecked] = useState(defaultNullChecked);

  // Sync state with URL changes (browser back/forward)
  useEffect(() => {
    const currentValue = getValueFromPath(path, where);
    const { defaultTrueChecked, defaultFalseChecked, defaultNullChecked } =
      getBooleanDefaultValue(currentValue, isNullable, nullCheckMode);

    setTrueChecked(defaultTrueChecked);
    setFalseChecked(defaultFalseChecked);
    setNullChecked(defaultNullChecked);
  }, [where, path, isNullable, nullCheckMode, getValueFromPath]);

  const applyNullCheckMode = (
    newWhere: any,
    newTrueChecked: boolean,
    newFalseChecked: boolean,
    isNullValue: boolean,
  ) => {
    // Both checked = no filter
    if (newTrueChecked && newFalseChecked) {
      return newWhere;
    }

    // Only one checked = apply filter
    if (newTrueChecked) {
      return addWhereClause(newWhere, [...path, '_is_null'], isNullValue);
    }
    if (newFalseChecked) {
      return addWhereClause(newWhere, [...path, '_is_null'], !isNullValue);
    }

    return newWhere;
  };

  const applyBooleanMode = (
    newWhere: any,
    newTrueChecked: boolean,
    newFalseChecked: boolean,
    newNullChecked: boolean,
  ) => {
    const allChecked =
      newTrueChecked && newFalseChecked && (newNullChecked || !isNullable);
    const noneChecked = !newTrueChecked && !newFalseChecked && !newNullChecked;

    // All or none = no filter
    if (allChecked || noneChecked) {
      return newWhere;
    }

    // Two checked
    if (newTrueChecked && newFalseChecked) {
      return addWhereClause(newWhere, [...path, '_in'], [true, false]);
    }
    if (newTrueChecked && newNullChecked) {
      return addWhereClause(newWhere, [...path, '_neq'], false);
    }
    if (newFalseChecked && newNullChecked) {
      return addWhereClause(newWhere, [...path, '_neq'], true);
    }

    // One checked
    if (newTrueChecked) {
      return addWhereClause(newWhere, [...path, '_eq'], true);
    }
    if (newFalseChecked) {
      return addWhereClause(newWhere, [...path, '_eq'], false);
    }
    if (newNullChecked) {
      return addWhereClause(newWhere, [...path, '_is_null'], true);
    }

    return newWhere;
  };

  const apply = (
    newTrueChecked: boolean,
    newFalseChecked: boolean,
    newNullChecked: boolean,
  ) => {
    let newWhere = deleteWhereClause(where, path, true);

    if (nullCheckMode === 'not-null') {
      newWhere = applyNullCheckMode(
        newWhere,
        newTrueChecked,
        newFalseChecked,
        false,
      );
    } else if (nullCheckMode === 'is-null') {
      newWhere = applyNullCheckMode(
        newWhere,
        newTrueChecked,
        newFalseChecked,
        true,
      );
    } else {
      newWhere = applyBooleanMode(
        newWhere,
        newTrueChecked,
        newFalseChecked,
        newNullChecked,
      );
    }

    setFiltersParams(newWhere);
  };

  const handleCheckboxChange = (
    type: 'true' | 'false' | 'null',
    checked: boolean,
  ) => {
    const newTrueChecked = type === 'true' ? checked : trueChecked;
    const newFalseChecked = type === 'false' ? checked : falseChecked;
    const newNullChecked = type === 'null' ? checked : nullChecked;

    if (type === 'true') {
      setTrueChecked(checked);
    }
    if (type === 'false') {
      setFalseChecked(checked);
    }
    if (type === 'null') {
      setNullChecked(checked);
    }

    apply(newTrueChecked, newFalseChecked, newNullChecked);
  };

  const handleDelete = () => {
    setTrueChecked(false);
    setFalseChecked(false);
    setNullChecked(false);
    const newWhere = deleteWhereClause(where, path, true);
    setFiltersParams(newWhere);
  };

  const hasActiveFilter = trueChecked || falseChecked || nullChecked;

  return (
    <FilterChip
      label={label}
      icon={<SquareCheck size={16} />}
      onDelete={hasActiveFilter ? handleDelete : undefined}
      disabled={disabled?.(where) ?? false}
      renderFilter={() => (
        <CheckboxFilterPanel>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={trueChecked}
                onChange={e => handleCheckboxChange('true', e.target.checked)}
              />
            }
            label={t('True')}
            sx={{ mx: 0.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={falseChecked}
                onChange={e => handleCheckboxChange('false', e.target.checked)}
              />
            }
            label={t('False')}
            sx={{ mx: 0.5 }}
          />
          {isNullable && nullCheckMode === 'disabled' && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={nullChecked}
                  onChange={e => handleCheckboxChange('null', e.target.checked)}
                />
              }
              label={t('Null')}
              sx={{ mx: 0.5 }}
            />
          )}
        </CheckboxFilterPanel>
      )}
    />
  );
};
